import { sessionCanMove, TimerCalendarItem } from '@utils/timerStart';
import { BumpContext } from '@utils/bump';
import { usesBumpers } from '@utils/settingsUtils';
import { CALENDAR_DAY_START_HOUR } from '@utils/calendar/calendarConstants';
import { unpackSourceEvents } from '../events/selectors';
import { reduceCalendarEventsToListObject } from '../../utils/reducerHelpers';
import { getType } from 'typesafe-actions';
import { createSelector } from 'reselect';
import { DATE_FORMAT, now, takeLatestDate, toMomentDate } from 'shovel-lib/utils/timeUtils';
import { CalendarEvent, CalendarEventType, DatesContainingTasks, TaskState } from 'shovel-lib/types';
import { RootState } from '@state/types';
import { getActiveTimer } from '@state/timer/selectors';
import { CalendarView } from './types';
import { buildWeekTimeBudget, shiftRangeToCalendarDays, WeekTimeEvent } from '../../utils/weekTimeBudget';
import {
  addDraggingClass,
  calculateStudyAndExtraTimeEvents,
  createPlannableStudyBlocksFilter,
  fromNowOn,
  asGoogleEvents,
  eventEndsToBreakAfter,
  extraTimeFilter,
  isCommitmentOrEvent,
  removeDraggingClasses,
  studyTimeFilter
} from '../../utils/calendar/calendarUtils';
import {
  IS_PLANNABLE,
  IS_PLANNABLE_OVERDUE,
  PLANNABLE_EVENT_CLASS_NAME,
  PLANNABLE_EVENT_GROUP_ID,
  PLANNABLE_OVERDUE_EVENT_CLASS_NAME
} from '../../utils/calendar/calendarConstants';
import {
  toBackgroundAwakeTime,
  toCalendarEvent,
  toExtraTimeEvent,
  toGoogleEventColorizeAsCourse,
  toStudyTimeEvent,
  toTaskEvent,
  toWarningAwakeTimeOverlapEvent
} from '../../utils/calendar/calendarEventConverters';
import { CalendarMode } from '../common/types';
import { createCommuteEvents, reduceArrayToObject } from '../../utils/reducerHelpers';
import { createEvent, deleteEvent, updateEvent, sourceEvents } from '../events/actions';
import { calendarData, movePlannedTask, planTask, resizePlannedTask } from './actions';
import { extractActionNameWithoutSuffix } from '../common/utils';
import { initialMonthFilterState } from '../settings/types';
import { MonthFilterT } from '../settings/types';
import { getGoogleEvents } from '../googleCalendar/actions';
import { groupByDueDate } from '../../utils/taskUtils';
import * as calendarActions from './actions';
import { termActions } from '../terms';
import unpackEvent from '@utils/unpackEvent';
import { isMonthFilterActive } from './utils';
import {
  constructStudyOrExtraTime,
  extractFreeTimeBlocks,
  minutesPopulatedWithEvents
} from 'shovel-lib/utils/calendarFreeBlocksUtils';
import { triggerIcsUpdates, unsyncCourseFromLMS } from '@state/ics/actions';
import { CourseSource } from '@state/ics/reducers';

const getCalendar = (state: RootState) => {
  const { dialog, ...data } = state.calendar;
  return { ...data, newEvents: state.event.events };
};

const getCalendarLoaderInfo = ({ calendar: { courses, activities, semesterInfo, view } }: RootState) => {
  return {
    view,
    courses,
    activities,
    semesterInfo
  };
};

const isCommitmentsLoading = ({ common }: RootState) =>
  common.loaders[extractActionNameWithoutSuffix(calendarActions.commitmentsData.request)];

const isCourseSyncInProgress = ({ common }: RootState) =>
  common.loaders[extractActionNameWithoutSuffix(termActions.createBatchCourses.request)];

const showsCompletedTasksInHeader = ({ settings: { userSettings } }: RootState) =>
  !!userSettings?.showCompletedTasks?.calendarHeader;

// The calendar header's tasks, without the completed ones unless the student shows those, worked out again only when
// the tasks or that setting change: lists filtered anew on every store change drew the header's days again each time.
const getHeaderDatesContainingTasks = createSelector(
  [({ calendar }: RootState) => calendar.datesContainingTasks, showsCompletedTasksInHeader],
  (datesContainingTasks, showCompleted) =>
    showCompleted ? datesContainingTasks : filterIncompleteTasks(datesContainingTasks)
);

const getHeaderTasksOnDay = createSelector(
  [({ calendar }: RootState) => calendar.tasksOnDay, showsCompletedTasksInHeader],
  // @ts-ignore
  (tasksOnDay, showCompleted) => (showCompleted ? tasksOnDay : tasksOnDay.filter(task => task.state !== TaskState.COMPLETED))
);

const getTasksHeaderInfo = (state: RootState) => {
  const { startDate, endDate, closeActivePopup, view, cyclicDayCount } = state.calendar;
  return {
    datesContainingTasks: getHeaderDatesContainingTasks(state),
    startDate,
    endDate,
    tasksOnDay: getHeaderTasksOnDay(state),
    closeActivePopup,
    view,
    cyclicDayCount
  };
};

const filterIncompleteTasks = (datesContainingTasks: DatesContainingTasks): DatesContainingTasks => {
  const filteredTasks: DatesContainingTasks = {};

  for (const [date, tasks] of Object.entries(datesContainingTasks)) {
    // @ts-ignore
    const incompleteTasks = tasks.filter(task => !task.isCompleted);

    if (incompleteTasks.length > 0) {
      filteredTasks[date] = incompleteTasks;
    }
  }

  return filteredTasks;
};

const getFilterFunction = (filter: MonthFilterT, isFilterActive: boolean) => {
  return event => {
    const isExam = event.type === 'EXAM';
    const isTask = event.type === CalendarEventType.TASK || isExam;
    return (
      !isFilterActive ||
      event.type === CalendarEventType.HOLIDAY ||
      (filter.customEvents && event.type === CalendarEventType.EVENT) ||
      (filter.exams ? isExam : filter.tasks && isTask) ||
      (filter.activityCorrelationIds.length > 0 && filter.activityCorrelationIds.includes(event.correlationId)) ||
      (filter.courseCorrelationIds.length > 0 && filter.courseCorrelationIds.includes(event.correlationId))
    );
  };
};

const getCalendarDialog = ({ calendar }: RootState) => calendar.dialog;

const getDefaultEventColor = ({ calendar }: RootState) => calendar.eventColor;

const separateAwakeTimesAndEvents = (
  acc: { events: CalendarEvent[]; awakeTimeEvents: CalendarEvent[] },
  sequence: CalendarEvent[],
  defaultEventColor: string,
  userSettings: any,
  theme: any
) => {
  if (sequence.length > 0) {
    if (sequence[0].type === CalendarEventType.AWAKE_TIME) {
      acc.awakeTimeEvents = [
        ...acc.awakeTimeEvents,
        ...sequence.map(event => toCalendarEvent(event, defaultEventColor, userSettings, theme))
      ];
    } else {
      acc.events = [...acc.events, ...sequence.map(event => toCalendarEvent(event, defaultEventColor, userSettings, theme))];
    }
  }
  return acc;
};

const getSemesterEventsAndAwakes = createSelector(
  [
    (state: RootState) => state.event.events,
    (state: RootState) => state.calendar.eventColor,
    (state: RootState) => state.settings.userSettings,
    (_, props) => props?.theme
  ],
  (stateEvents, defaultEventColor, userSettings, theme) => {
    // Events can arrive before userSettings; toCalendarEvent needs courseEventStyle etc.
    if (!userSettings) {
      return { events: [], awakeTimeEvents: [] };
    }
    return Object.values(stateEvents).reduce(
      (result: { events: CalendarEvent[]; awakeTimeEvents: CalendarEvent[] }, currentValue: any) =>
        separateAwakeTimesAndEvents(result, currentValue, defaultEventColor, userSettings, theme),
      { events: [], awakeTimeEvents: [] }
    );
  }
);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * When today began on the calendar (its days run 4am to 4am), in the term's time zone: changes once a day, so what
 * depends on it is worked out again each day.
 */
const calendarDayStart = () => {
  const current = toMomentDate(new Date());
  const start = current
    .clone()
    .startOf('day')
    .add(CALENDAR_DAY_START_HOUR, 'hours');
  return (current.isBefore(start) ? start.subtract(1, 'day') : start).valueOf();
};

/**
 * Events, classes and awake times from yesterday to a week ahead, whatever dates the calendar shows: what ▶ plans round
 * (bump.ts). The calendar itself unpacks only the dates on screen (`state.event.events`), so a start in day view, or
 * on a Saturday with Sunday to come, missed what was in the way.
 */
const getHorizonEventsAndAwakes = createSelector(
  [
    (state: RootState) => state.event.sourceEvents,
    (state: RootState) => state.calendar.eventColor,
    (state: RootState) => state.settings.userSettings,
    (_, props) => props?.theme,
    calendarDayStart
  ],
  (sourceEvents, defaultEventColor, userSettings, theme, dayStart) => {
    const empty: { events: CalendarEvent[]; awakeTimeEvents: CalendarEvent[] } = { events: [], awakeTimeEvents: [] };
    if (!userSettings || !sourceEvents?.length) return empty;
    const unpacked = unpackSourceEvents(sourceEvents, new Date(dayStart - DAY_MS), new Date(dayStart + 8 * DAY_MS));
    return Object.values(reduceCalendarEventsToListObject(unpacked)).reduce(
      (result, sequence: any) => separateAwakeTimesAndEvents(result, sequence, defaultEventColor, userSettings, theme),
      empty
    );
  }
);

const getHorizonCommutes = createSelector(
  [getHorizonEventsAndAwakes, (state: RootState) => state.settings.userSettings, (_, props) => props?.theme],
  (horizon, userSettings, theme) =>
    userSettings
      ? createCommuteEvents(
          horizon.events.filter(e => isCommitmentOrEvent(e.type)),
          userSettings,
          theme
        )
      : []
);

// How far Bumpers looks when the term has no end date: half a year.
const BUMPERS_FALLBACK_DAYS = 183;

const termEndOf = (semesterInfo: any, dayStart: number) =>
  semesterInfo?.calendarMaxDate
    ? toMomentDate(semesterInfo.calendarMaxDate).valueOf()
    : dayStart + BUMPERS_FALLBACK_DAYS * DAY_MS;

/** Events, classes and awake times from yesterday to the term's end, for Bumpers. */
const getTermEventsAndAwakes = createSelector(
  [
    (state: RootState) => state.event.sourceEvents,
    (state: RootState) => state.calendar.eventColor,
    (state: RootState) => state.settings.userSettings,
    (_, props) => props?.theme,
    (state: RootState) => state.calendar.semesterInfo,
    calendarDayStart
  ],
  (sourceEvents, defaultEventColor, userSettings, theme, semesterInfo, dayStart) => {
    const empty: { events: CalendarEvent[]; awakeTimeEvents: CalendarEvent[] } = { events: [], awakeTimeEvents: [] };
    if (!userSettings || !sourceEvents?.length) return empty;
    const termEnd = Math.max(termEndOf(semesterInfo, dayStart), dayStart + 8 * DAY_MS);
    const unpacked = unpackSourceEvents(sourceEvents, new Date(dayStart - DAY_MS), new Date(termEnd + DAY_MS));
    return Object.values(reduceCalendarEventsToListObject(unpacked)).reduce(
      (result, sequence: any) => separateAwakeTimesAndEvents(result, sequence, defaultEventColor, userSettings, theme),
      empty
    );
  }
);

const getTermCommutes = createSelector(
  [getTermEventsAndAwakes, (state: RootState) => state.settings.userSettings, (_, props) => props?.theme],
  (term, userSettings, theme) =>
    userSettings
      ? createCommuteEvents(
          term.events.filter(e => isCommitmentOrEvent(e.type)),
          userSettings,
          theme
        )
      : []
);

/**
 * What Bumpers needs besides the blocks: the awake windows to the term's end, when each day ends (4am, in the term's
 * time zone), the term's end, and its minimum study block.
 */
export const getBumpersContext = createSelector(
  [
    (state: RootState, props: any) => getTermEventsAndAwakes(state, props).awakeTimeEvents,
    (state: RootState) => state.calendar.semesterInfo,
    calendarDayStart
  ],
  (awakeTimeEvents, semesterInfo, dayStart): BumpContext => {
    const time = (value: any) => toMomentDate(value).valueOf();
    const termEnd = termEndOf(semesterInfo, dayStart);
    const dayEnds: number[] = [];
    const day = toMomentDate(new Date(dayStart));
    do {
      day.add(1, 'day');
      dayEnds.push(day.valueOf());
    } while (day.valueOf() < termEnd + DAY_MS);
    return {
      awake: awakeTimeEvents
        .map(event => ({ start: time(event.start), end: time(event.end) }))
        .sort((a, b) => a.start - b.start),
      dayEnd: dayEnds[0],
      horizonEnd: termEnd,
      minBlock: semesterInfo?.minimumStudyBlock || 30,
      dayEnds,
      dayStart
    };
  }
);

/**
 * What ▶ needs besides the calendar's blocks to bump the day down (bump.ts): the awake windows from today to a week
 * ahead, when today ends, how far to look, and the term's minimum study block.
 */
export const getSmartStartContext = createSelector(
  [
    (state: RootState, props: any) => getHorizonEventsAndAwakes(state, props).awakeTimeEvents,
    (state: RootState) => state.calendar.semesterInfo,
    calendarDayStart
  ],
  (awakeTimeEvents, semesterInfo, dayStart): BumpContext => {
    const time = (value: any) => toMomentDate(value).valueOf();
    const dayEnd = toMomentDate(new Date(dayStart))
      .add(1, 'day')
      .valueOf();
    const termEnd = semesterInfo?.calendarMaxDate ? time(semesterInfo.calendarMaxDate) : Infinity;
    return {
      awake: awakeTimeEvents
        .map(event => ({ start: time(event.start), end: time(event.end) }))
        .sort((a, b) => a.start - b.start),
      dayEnd,
      horizonEnd: Math.min(dayStart + 7 * DAY_MS, termEnd),
      minBlock: semesterInfo?.minimumStudyBlock || 30
    };
  }
);

/**
 * What a late start or Keep going bumps with (bump.ts): the calendar a week ahead, or with Bumpers on (Petr, 2026-10-29:
 * the same as a drag) to the term's end, `chain` carrying the bump on into the next days.
 */
export const getBumpInputs = (state: RootState, props: { theme: any }) => {
  const chain = usesBumpers(state.settings.userSettings as any);
  return {
    chain,
    items: chain ? getBumpersCalendarItems(state, props) : getTimerCalendarItems(state, props),
    context: chain ? getBumpersContext(state, props) : getSmartStartContext(state, props)
  };
};

/** The student's awake times; when each one ends is their bedtime that night. */
export const getAwakeTimeEvents = (state: RootState, props: { theme: any }) =>
  getSemesterEventsAndAwakes(state, props).awakeTimeEvents;

const getPlannedEvents = createSelector(
  [
    (state: RootState) => state.calendar.plannedEvents,
    (state: RootState) => state.calendar.eventColor,
    (state: RootState) => state.settings.userSettings,
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(calendarData.request)],
    (_, props) => props?.theme
  ],
  (plannedEventsObject, defaultEventColor, userSettings, dataLoading, theme) => {
    return dataLoading || !userSettings
      ? []
      : Object.values(plannedEventsObject).map((event: any) =>
          toCalendarEvent(event, defaultEventColor, userSettings, theme)
        );
  }
);

const getTaskEvents = createSelector(
  [(state: RootState) => state.calendar.datesContainingTasks, (_, props) => props?.theme],
  (datesContainingTasks, theme) => {
    return Object.values(datesContainingTasks || []).reduce(
      (acc: CalendarEvent[], events: any) => [...acc, ...events.map(task => toTaskEvent(task, theme))],
      []
    );
  }
);

const getDatesContainingTasks = (state: RootState) => state.calendar.datesContainingTasks;

const getCommuteTimes = createSelector(
  [
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(sourceEvents.request)],
    getSemesterEventsAndAwakes,
    (state: RootState) => state.settings.userSettings,
    (_, props) => props?.theme
  ],
  (eventsListLoading, stateEvents, userSettings, theme) => {
    return eventsListLoading || !userSettings
      ? []
      : createCommuteEvents(
          stateEvents.events.filter(e => isCommitmentOrEvent(e.type)),
          userSettings,
          theme
        );
  }
);

const getFreeTimeEvents = createSelector(
  [
    (state: RootState) => state.calendar.semesterInfo,
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(calendarData.request)],
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(sourceEvents.request)],
    (state: RootState) => state.calendar.googleEvents,
    getSemesterEventsAndAwakes,
    getPlannedEvents,
    getCommuteTimes
  ],
  (semesterInfo, calendarDataLoading, eventsListLoading, googleEvents, stateEvents, plannedEvents, commuteTimes) => {
    if (!semesterInfo || calendarDataLoading || eventsListLoading || stateEvents.awakeTimeEvents.length === 0) return [];
    const { firstAwakeTime, lastAwakeTime } = (stateEvents.awakeTimeEvents as CalendarEvent[]).reduce(
      (acc, curr) => {
        acc.firstAwakeTime = curr.start < acc.firstAwakeTime.start ? curr : acc.firstAwakeTime;
        acc.lastAwakeTime = curr.end < acc.lastAwakeTime.end ? curr : acc.lastAwakeTime;
        return acc;
      },
      {
        firstAwakeTime: stateEvents.awakeTimeEvents[0] as CalendarEvent,
        lastAwakeTime: stateEvents.awakeTimeEvents[0] as CalendarEvent
      }
    );

    const calendarStart =
      firstAwakeTime && semesterInfo.calendarMinDate.isAfter(firstAwakeTime.start)
        ? toMomentDate(firstAwakeTime.start)
        : semesterInfo.calendarMinDate;
    const calendarEnd =
      lastAwakeTime && semesterInfo.calendarMaxDate.isBefore(lastAwakeTime.end)
        ? toMomentDate(lastAwakeTime.end)
        : semesterInfo.calendarMaxDate;

    return calculateStudyAndExtraTimeEvents(
      {
        calendarStartDate: calendarStart,
        calendarEndDate: calendarEnd
      },
      semesterInfo,
      [...stateEvents.events, ...plannedEvents, ...commuteTimes, ...googleEvents],
      stateEvents.awakeTimeEvents
    );
  }
);

export const getFreeTimeEventsWeek = createSelector(
  [
    (state: RootState) => state.calendar.semesterInfo,
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(calendarData.request)],
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(sourceEvents.request)],
    (state: RootState) => state.calendar.googleEvents,
    (state: RootState, props: any) => getSemesterEventsAndAwakes?.(state, props),
    (state: RootState, props: any) => getPlannedEvents?.(state, props),
    (state: RootState, props: any) => getCommuteTimes?.(state, props),
    (state: RootState, props: any) => getTaskEvents?.(state, props),
    (state: RootState) => state.calendar.startDate,
    (state: RootState) => state.calendar.endDate,
    (state: RootState) => state.calendar.view === CalendarView.MONTH
  ],
  (
    semesterInfo,
    calendarDataLoading,
    eventsListLoading,
    googleEvents,
    stateEvents,
    plannedEvents,
    commuteTimes,
    taskEvents,
    startDate,
    endDate,
    isMonthView
  ) => {
    if (
      calendarDataLoading ||
      eventsListLoading ||
      !semesterInfo ||
      stateEvents.awakeTimeEvents.length === 0 ||
      !startDate ||
      !endDate ||
      isMonthView
    )
      return [];

    const start = toMomentDate(startDate);
    const end = toMomentDate(endDate);

    const calendarStart = start.isBefore(semesterInfo.calendarMinDate) ? semesterInfo.calendarMinDate : start;

    const calendarEnd = end.isAfter(semesterInfo.calendarMaxDate) ? semesterInfo.calendarMaxDate : end;

    if (calendarEnd.isBefore(calendarStart)) return [];

    const populatedMinutes = minutesPopulatedWithEvents(
      [
        ...stateEvents.events,
        ...plannedEvents,
        ...commuteTimes,
        ...googleEvents,
        ...taskEvents.filter(e => e.type !== CalendarEventType.TASK)
      ],
      stateEvents.awakeTimeEvents,
      calendarStart,
      calendarEnd
    );

    return extractFreeTimeBlocks(populatedMinutes).map(block =>
      constructStudyOrExtraTime(block, semesterInfo, calendarStart.toDate(), now())
    );
  }
);

const EXCLUDED_PLANNING_EVENT_TYPES = new Set([
  CalendarEventType.STUDY_TIME,
  CalendarEventType.EXTRA_TIME,
  CalendarEventType.AWAKE_TIME,
  CalendarEventType.SLEEP_TIME,
  CalendarEventType.TASK
]);

export const getBusyEventsForWeekPlanning = createSelector(
  [
    (state: RootState, props: any) => getSemesterEventsAndAwakes?.(state, props),
    (state: RootState, props: any) => getPlannedEvents?.(state, props),
    (state: RootState, props: any) => getCommuteTimes?.(state, props),
    (state: RootState) => state.calendar.googleEvents,
    (state: RootState) => state.calendar.courses,
    (state: RootState) => state.calendar.activities,
    (state: RootState) => state.calendar.startDate,
    (state: RootState) => state.calendar.endDate
  ],
  (stateEvents, plannedEvents, commuteTimes, googleEvents, courses, activities, startDate, endDate) => {
    if (!startDate || !endDate || !stateEvents) return [];

    const rangeStart = toMomentDate(startDate);
    const rangeEnd = toMomentDate(endDate);
    const namesById = new Map<number, string>();
    (courses || []).forEach(course => namesById.set(course.id, course.name));
    (activities || []).forEach(activity => namesById.set(activity.id, activity.name));

    const busyEvents: CalendarEvent[] = [...stateEvents.events, ...plannedEvents, ...commuteTimes, ...googleEvents];

    return busyEvents
      .filter(event => event && !EXCLUDED_PLANNING_EVENT_TYPES.has(event.type))
      .filter(event => {
        const start = toMomentDate(event.start);
        const end = toMomentDate(event.end);
        return end.isAfter(rangeStart) && start.isBefore(rangeEnd);
      })
      .map(event => ({
        title: event.title,
        type: event.type,
        start: event.start,
        end: event.end,
        courseName: event.task?.courseName || (event.courseId != null ? namesById.get(event.courseId) : undefined),
        courseId: event.courseId ?? undefined
      }));
  }
);

/**
 * The calendar's blocks as the timer's smart default sees them (utils/timerStart.ts): study sessions, which it may move,
 * and the busy events and commutes, which it stops for. Free events ("free" on the block) don't count. Commutes carry
 * their event's name, for "Time to leave for Lab".
 */
export const getTimerCalendarItems = createSelector(
  [
    (state: RootState, props: any) => getHorizonEventsAndAwakes(state, props),
    (state: RootState) => state.calendar.plannedEvents,
    (state: RootState, props: any) => getHorizonCommutes(state, props),
    (state: RootState) => state.calendar.googleEvents
  ],
  (stateEvents, plannedEvents, commuteTimes, googleEvents): TimerCalendarItem[] =>
    toTimerCalendarItems(stateEvents, plannedEvents, commuteTimes, googleEvents)
);

/**
 * The same blocks to the term's end, for Bumpers (timer spec "Bumpers"): a drag's chain carries on into the next days
 * as far as it has to. Unpacked once per change to the events, not as the drag moves.
 */
export const getBumpersCalendarItems = createSelector(
  [
    (state: RootState, props: any) => getTermEventsAndAwakes(state, props),
    (state: RootState) => state.calendar.plannedEvents,
    (state: RootState, props: any) => getTermCommutes(state, props),
    (state: RootState) => state.calendar.googleEvents,
    (state: RootState) => getActiveTimer(state)?.plannedTaskId
  ],
  (stateEvents, plannedEvents, commuteTimes, googleEvents, timerSessionId): TimerCalendarItem[] =>
    // A session whose timer runs or is paused stays where it is: its time isn't saved on it until it's ticked.
    toTimerCalendarItems(stateEvents, plannedEvents, commuteTimes, googleEvents).map(item =>
      item.kind === 'session' && timerSessionId !== undefined && item.id === String(timerSessionId)
        ? { ...item, movable: false }
        : item
    )
);

// The student's own Activities, which ▶ may run through (bump.ts planBump); the old meal and me-time types are them too.
const TIMER_FLEXIBLE_EVENT_TYPES = new Set<string>([CalendarEventType.ACTIVITY, 'MEAL', 'ME_TIME']);

function toTimerCalendarItems(
  stateEvents: { events: CalendarEvent[] } | undefined,
  plannedEvents: any,
  commuteTimes: any[],
  googleEvents: any[]
): TimerCalendarItem[] {
  {
    const events: any[] = [...(stateEvents?.events || []), ...(googleEvents || [])].filter(
      event => event && !EXCLUDED_PLANNING_EVENT_TYPES.has(event.type) && event.busy !== false && !event.allDay
    );
    const titleOf = (id: any, start: any) =>
      events.find(event => String(event.id) === String(id) && toMomentDate(event.start).isSame(toMomentDate(start)))?.title;
    const time = (value: any) => toMomentDate(value).valueOf();
    return [
      ...Object.values(plannedEvents || {}).map((session: any) => ({
        id: String(session.id),
        kind: 'session' as const,
        start: time(session.start),
        end: time(session.end),
        title: session.title,
        taskId: session.taskId,
        dueDate: session.dueDate ? time(session.dueDate) : undefined,
        // Time on it, ticked, or its task done: it stays where it is when the day is bumped down.
        movable: sessionCanMove(session)
      })),
      ...events.map(event => ({
        id: String(event.id),
        kind: 'fixed' as const,
        start: time(event.start),
        end: time(event.end),
        title: event.title,
        ...(TIMER_FLEXIBLE_EVENT_TYPES.has(event.type) ? { flexible: true } : {})
      })),
      ...(commuteTimes || [])
        .filter((commute: any) => commute.busy !== false)
        .map((commute: any) => ({
          id: `commute-${commute.groupId || commute.id}`,
          kind: 'commute' as const,
          start: time(commute.start),
          end: time(commute.end),
          title:
            commute.type === CalendarEventType.COMMUTE_BEFORE
              ? titleOf(commute.id, commute.originalEventStart || commute.end)
              : undefined
        }))
    ];
  }
}

/** Where each event in the shown dates ends, for the break Auto-plan leaves after it; see `eventEndsToBreakAfter`. */
export const getEventEndsForWeekPlanning = createSelector(
  [
    (state: RootState, props: any) => getSemesterEventsAndAwakes?.(state, props),
    (state: RootState, props: any) => getCommuteTimes?.(state, props),
    (state: RootState, props: any) => getTaskEvents?.(state, props),
    (state: RootState) => state.calendar.googleEvents,
    (state: RootState) => state.calendar.startDate,
    (state: RootState) => state.calendar.endDate
  ],
  (stateEvents, commuteTimes, taskEvents, googleEvents, startDate, endDate): Date[] => {
    if (!startDate || !endDate || !stateEvents) return [];
    return eventEndsToBreakAfter(
      [...stateEvents.events, ...asGoogleEvents(googleEvents), ...(taskEvents || [])],
      commuteTimes || [],
      startDate,
      endDate
    );
  }
);

const getCalendarEvents = createSelector(
  [
    (state: RootState) => state.calendar.semesterInfo,
    (state: RootState) => state.calendar.isDragging,
    (state: RootState) => state.calendar.draggingTask,
    (state: RootState) => state.calendar.eventColor,
    (state: RootState, props: any) => props?.mode === CalendarMode.AWAKE,
    (state: RootState) => state.calendar.view,
    (state: RootState) => state.calendar.googleEvents,
    getSemesterEventsAndAwakes,
    (state: RootState) => state.calendar.plannedEvents,
    getTaskEvents,
    getCommuteTimes,
    getFreeTimeEventsWeek,
    (state: RootState) => state.course.googleCalendarId,
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(calendarData.request)],
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(sourceEvents.request)],
    (state: RootState) => state.common.loaders[extractActionNameWithoutSuffix(getGoogleEvents.request)],
    (state: RootState) => state.settings.userSettings?.monthFilter || initialMonthFilterState,
    (_, props: any) => props?.theme,
    (state: RootState) => state.calendar.courses,
    (state: RootState) => state.calendar.activities,
    (state: RootState) => state.settings.userSettings
  ],
  (
    semesterInfo,
    isDragging,
    draggingTask,
    defaultEventColor,
    isAwakeMode,
    view,
    googleEvents,
    stateEvents,
    plannedTaskEvents,
    taskEvents,
    commuteTimes,
    freeTimeEvents,
    courseGoogleCalendarId,
    calendarDataLoading,
    eventsListLoading,
    googleEventsLoading,
    monthFilter,
    theme,
    courses,
    activities,
    userSettings
  ) => {
    if (calendarDataLoading || eventsListLoading || googleEventsLoading || !semesterInfo || !userSettings) return [];

    const isMonthView = view === CalendarView.MONTH;
    const isFilterActive = isMonthFilterActive(monthFilter);

    let eventsFilter;
    if (isMonthView) {
      eventsFilter = getFilterFunction(monthFilter, isFilterActive);
    } else {
      eventsFilter = event => {
        if (event.type !== CalendarEventType.TASK) {
          // always display non-task events
          return true;
        }
        // handle task event
        if (!userSettings?.showFlags) {
          // if showFlags is toggled off, don't display tasks
          return false;
        }
        if (!event.task?.isCompleted) {
          // show active tasks
          return true;
        }
        // show completed tasks if personalization option is toggled on
        return userSettings?.showCompletedTasks?.flags;
      };
    }

    const { events, awakeTimeEvents } = stateEvents;

    if (isAwakeMode) {
      return awakeTimeEvents;
    }

    const availableDaysForPlanning: { [key: string]: CalendarEvent } = {};
    const availableDaysForPlanningOverdue: { [key: string]: CalendarEvent } = {};

    let plannedEvents = eventsListLoading
      ? []
      : Object.values(plannedTaskEvents)
          .filter((event: any) => toMomentDate(event.end).diff(event.start, 'minutes') > 0)
          .map((event: any) => toCalendarEvent(event, defaultEventColor, userSettings, theme));

    // The free time a dragged task could go into is marked on the events themselves, so the marks are cleared again
    // here whenever the calendar is worked out, not only when the next drag starts: a task dropped back where it came
    // from left every block marked, and the free time's hover stopped working until the next drag (Petr).
    if (!isMonthView) {
      freeTimeEvents.forEach(event => {
        if (event.groupId === PLANNABLE_EVENT_GROUP_ID) removeDraggingClasses(event);
      });
    }

    if (isDragging && draggingTask) {
      const { taskId, dueDate } = draggingTask;

      const plannableStudyBlocksFilter = createPlannableStudyBlocksFilter(
        takeLatestDate(semesterInfo.calendarMinDate, now()),
        dueDate
      );

      if (!isMonthView) {
        freeTimeEvents.forEach(event => {
          if (plannableStudyBlocksFilter(event) === IS_PLANNABLE) {
            addDraggingClass(event, PLANNABLE_EVENT_GROUP_ID, PLANNABLE_EVENT_CLASS_NAME);
          } else if (plannableStudyBlocksFilter(event) === IS_PLANNABLE_OVERDUE) {
            addDraggingClass(event, PLANNABLE_EVENT_GROUP_ID, PLANNABLE_OVERDUE_EVENT_CLASS_NAME);
          }
        });
      }
    }

    // needed for rendering sleep times if there are no awake times in the week
    const awakeTimes =
      awakeTimeEvents.length > 0
        ? awakeTimeEvents
        : [
            {
              start: semesterInfo.calendarMaxDate.toDate(),
              end: semesterInfo.calendarMaxDate.toDate(),
              type: CalendarEventType.AWAKE_TIME,
              classNames: ['awake-time-event'],
              startAsDate: semesterInfo.calendarMaxDate.toDate(),
              endAsDate: semesterInfo.calendarMaxDate.toDate()
            }
          ];

    let filteredEvents = events.filter(eventsFilter);
    if (isMonthView) {
      plannedEvents = plannedEvents.map(e => ({ ...e, display: 'background' }));
    }
    filteredEvents = [...filteredEvents, ...plannedEvents];
    const filteredTaskEvents = taskEvents.filter(eventsFilter);
    const filteredCommuteTimes = !isMonthView ? commuteTimes : [];
    const extraTime = !isMonthView ? freeTimeEvents.filter(extraTimeFilter).map(toExtraTimeEvent) : [];
    // While a task is dragged, free time the now line is inside is marked only from the line down: the part that has
    // gone can't take it (Petr).
    const studyTime = !isMonthView
      ? freeTimeEvents
          .filter(studyTimeFilter)
          .map(toStudyTimeEvent)
          .map(event => (isDragging && event.groupId === PLANNABLE_EVENT_GROUP_ID ? fromNowOn(event) : event))
      : [];
    const coursesByCalendarId = reduceArrayToObject([...courses, ...activities], 'googleCalendarId');
    const filteredGoogleEvents = (isMonthView && isFilterActive && !monthFilter.googleEvents ? [] : googleEvents).map(e =>
      // @ts-ignore
      toGoogleEventColorizeAsCourse(e, coursesByCalendarId[e.googleCalendarId], userSettings, theme)
    );
    // if it is week or cyclic view then group of tasks with same dueDate display as one, with common cal event props, list of tasks as extendedProps
    const tasks = isMonthView
      ? filteredTaskEvents
      : Object.entries(groupByDueDate(filteredTaskEvents)).map(([key, value], index) => {
          return (value as CalendarEvent[])[0].type === CalendarEventType.TASK
            ? {
                //@ts-ignore
                backgroundColor: value[0].backgroundColor,
                borderColor: null,
                busy: false,
                classNames: ['task-event'],
                colorHex: null,
                correlationId: null,
                // A background event, so the calendar never sets it beside a session or class and halves their width
                // (Petr, 2026-10-30); calendarStyles.ts lifts its line and dot back on top of the blocks.
                display: 'background',
                editable: false,
                //@ts-ignore
                end: value[0].end,
                id: index,
                start: key,
                tasks: value,
                textColor: null,
                title: '',
                type: CalendarEventType.TASK
              }
            : (value as CalendarEvent[])[0];
        });

    return [
      ...Object.values(availableDaysForPlanning),
      ...Object.values(availableDaysForPlanningOverdue),
      ...awakeTimes.map(e => toBackgroundAwakeTime(e, theme?.sleepTimeBackgroundColor)),
      ...filteredEvents.map(e => {
        if (e.type === CalendarEventType.HOLIDAY) {
          return e;
        }
        if (!isWrappedWithAwakeTime(awakeTimes, e)) {
          return toWarningAwakeTimeOverlapEvent(e);
        }
        return e;
      }),
      ...filteredGoogleEvents,
      ...filteredCommuteTimes.map(e => (!isWrappedWithAwakeTime(awakeTimes, e) ? toWarningAwakeTimeOverlapEvent(e) : e)),
      ...tasks,
      ...studyTime,
      ...extraTime
    ];
  }
);

const isWrappedWithAwakeTime = (awakeTimes: any[], event: any) => {
  return awakeTimes.some(
    at => at.startAsDate.getTime() <= event.startAsDate.getTime() && at.endAsDate.getTime() >= event.endAsDate.getTime()
  );
};

const isOverlapped = (plannedEvent: CalendarEvent, events: CalendarEvent[]) => {
  return events
    .filter(e => e.id !== plannedEvent.id && e.busy)
    .some(
      e =>
        (e.start <= plannedEvent.start && e.end > plannedEvent.start) ||
        (e.start < plannedEvent.end && e.end >= plannedEvent.end) ||
        (plannedEvent.start <= e.start && plannedEvent.end >= e.end)
    );
};

const getHolidays = createSelector(
  [(state: RootState) => state.event.events],
  events =>
    Object.values(events).reduce(
      (holidays: CalendarEvent[], events: CalendarEvent[]) =>
        events.length > 0 && events[0].type === CalendarEventType.HOLIDAY ? [...holidays, events[0]] : holidays,
      []
    ) as CalendarEvent[]
);

const getCalendarInfo = ({ calendar }: RootState) => {
  return calendar.semesterInfo;
};

const getCalendarView = ({ calendar }: RootState) => {
  return calendar.view;
};

const getDraggingTask = ({ calendar }: RootState) => calendar.draggingTask;

const getCommitments = (state: RootState) => {
  const { activities, courses } = state.calendar;
  return { activities, courses };
};

// The left sidebar's course and activity list is drawn from this, and a new object each time the store changed drew the
// whole list again on every change - about ten times while one task's estimate saves.
const getCommitmentsState = createSelector(
  [
    (state: RootState) => state.calendar.activities,
    (state: RootState) => state.calendar.courses,
    (state: RootState) => state.calendar.eventColor
  ],
  (activities, courses, eventColor) => ({ activities, courses, eventColor })
);

const getEventError = ({ common }: RootState) => {
  const updateEventType = getType(updateEvent.failure).split('_FAILURE')[0];
  return common.errors[updateEventType];
};

const eventsLoading = ({ common }: RootState) => {
  const updateEventType = getType(updateEvent.request).split('_REQUEST')[0];
  const createEventType = getType(createEvent.request).split('_REQUEST')[0];
  const deleteEventType = getType(deleteEvent.request).split('_REQUEST')[0];
  return common.loaders[createEventType] || common.loaders[updateEventType] || common.loaders[deleteEventType];
};

const plannedUpdateInProgress = ({ common }: RootState) => {
  const planTaskType = getType(planTask.request).split('_REQUEST')[0];
  const movePlannedTaskType = getType(movePlannedTask.request).split('_REQUEST')[0];
  const resizePlannedTaskType = getType(resizePlannedTask.request).split('_REQUEST')[0];
  return common.loaders[movePlannedTaskType] || common.loaders[resizePlannedTaskType] || common.loaders[planTaskType];
};

const isCalendarDataLoading = (state: RootState) =>
  state.common.loaders[extractActionNameWithoutSuffix(calendarData.request)];

const getMonthFilterState = ({ calendar, settings }: RootState) => ({
  filter: settings.userSettings?.monthFilter || initialMonthFilterState,
  activities: calendar.activities,
  courses: calendar.courses
});

const getCalendarStartDate = ({ calendar }: RootState) => calendar.startDate;
const getCalendarEndDate = ({ calendar }: RootState) => calendar.endDate;

// A copy made once per change of the calendars, not on every call: the same list, so the left sidebar isn't drawn again.
const getGoogleCalendars = createSelector([({ calendar }: RootState) => calendar.googleCalendars], googleCalendars => [
  ...googleCalendars
]);

export const getConnectedGoogleCalendars = createSelector(
  [
    ({ calendar }: RootState) => calendar.googleCalendars,
    ({ calendar }: RootState) => calendar.courses,
    ({ calendar }: RootState) => calendar.activities
  ],
  (googleCalendars, courses, activities) => {
    return googleCalendars.map(gc => {
      const connectedCourse =
        courses.find(c => c.googleCalendarId === gc.googleCalendarId) ??
        activities.find(c => c.googleCalendarId === gc.googleCalendarId);
      return connectedCourse
        ? {
            ...gc,
            connectedCourseName: connectedCourse.name
          }
        : gc;
    });
  }
);

const getEventApplyDialogState = ({ calendar }: RootState) => calendar.eventApplyDialog;

const isApplyDialogOpen = ({ calendar }: RootState) => calendar.eventApplyDialog.open;

const isPlanBlockDialogOpen = ({ calendar }: RootState) => calendar.planBlockDialog.opened;

const getPlanBlockDialogState = ({ calendar }: RootState) => calendar.planBlockDialog;

export const getImportCoursePreview = ({ calendar }: RootState) => calendar.importCourseDialog;

const isPlanSidebarCollapsed = ({ calendar }: RootState) => calendar.planSidebarCollapsed;

const getCyclicDayCount = ({ calendar }: RootState) => calendar.cyclicDayCount;

const getAllCourses = ({ calendar }: RootState) => calendar.courses;

const getAllActivities = ({ calendar }: RootState) => calendar.activities;

const getPlannedEvent = ({ calendar }: RootState, id: number) => calendar.plannedEvents[id];

const getClassTimes = createSelector(
  [
    (state: RootState) => state.event.sourceEvents,
    (state: RootState, props: any) =>
      [...state.calendar.courses, ...state.calendar.activities].find(c => c.id === props.courseId),
    (state: RootState, props: any) => !!state.calendar.courses.find(c => c.id === props.courseId),
    (_, props: any) => props?.theme
  ],
  (sourceEvents, course, isCourse, theme) => {
    if (!course) return { colorHex: undefined, classes: {} };
    return {
      colorHex: course.colorHex,
      isCourse,
      classes: sourceEvents
        .filter(e => e.correlationId === course.correlationId)
        .flatMap(c => unpackEvent(c))
        .map(c => c.start)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
        .reduce((acc, datetime) => {
          const date = toMomentDate(datetime).format(DATE_FORMAT);
          const times = acc[date] || [];
          times.push(datetime);
          acc[date] = times;
          return acc;
        }, {})
    };
  }
);

const getConnectedCalendarAndPlatformsForCourse = ({ calendar }: RootState, courseId: number) => {
  const course = calendar.courses.find(c => c.id === courseId);
  return {
    googleCalendarId: course?.googleCalendarId,
    parsedCourses: course?.parsedCourses
  };
};

const LMS_PLATFORMS = [CourseSource.CANVAS, CourseSource.BRIGHTSPACE, CourseSource.MOODLE];

const getConnectedPlatforms = ({ calendar }: RootState) => {
  const parsedCourses = calendar.courses.map(c => c.parsedCourses || []).flat();
  const platforms = parsedCourses
    .map(pc => pc.type)
    .filter((platform): platform is CourseSource => !!platform && LMS_PLATFORMS.includes(platform as CourseSource));
  return Array.from(new Set(platforms));
};

const isCheckingForPlatformUpdates = ({ common }: RootState) => {
  const triggerIcsUpdatesType = extractActionNameWithoutSuffix(triggerIcsUpdates.request);
  return common.loaders[triggerIcsUpdatesType];
};

const isUnsyncCourseFromLmsInProgress = ({ common }: RootState) => {
  const unsyncCourseFromLMSType = extractActionNameWithoutSuffix(unsyncCourseFromLMS.request);
  return common.loaders[unsyncCourseFromLMSType];
};

const isPlanThisWeekInProgress = ({ common }: RootState) =>
  !!common.loaders[extractActionNameWithoutSuffix(calendarActions.planThisWeek.request)];

const isUnplanWeekInProgress = ({ common }: RootState) =>
  !!common.loaders[extractActionNameWithoutSuffix(calendarActions.unplanWeek.request)];

export const getWeekTimeBudget = createSelector(
  [
    (state: RootState) => state.event.events,
    (state: RootState) => state.calendar.plannedEvents,
    (state: RootState) => state.calendar.googleEvents,
    (state: RootState) => state.calendar.courses,
    (state: RootState) => state.calendar.activities,
    (state: RootState) => state.calendar.startDate,
    (state: RootState) => state.calendar.endDate,
    (state: RootState) => state.calendar.view
  ],
  (sourceEventsById, plannedEventsById, googleEvents, courses, activities, startDate, endDate, view) => {
    if (!startDate || !endDate || view === CalendarView.MONTH) return null;
    const range = shiftRangeToCalendarDays(startDate, endDate);
    if (!range) return null;
    const events: WeekTimeEvent[] = [
      ...Object.values(sourceEventsById || {}).flat(),
      ...Object.values(plannedEventsById || {}),
      ...(googleEvents || []).map(event => ({
        start: event.start,
        end: event.end,
        type: CalendarEventType.GOOGLE_EVENT,
        busy: event.busy,
        allDay: event.allDay
      }))
    ];
    // Read when the calendar's data changes, which is when it is redrawn: a session that ends in the meantime turns
    // missed with the next change.
    return buildWeekTimeBudget(events, range.from, range.to, { courses, activities }, new Date());
  }
);

export {
  getCalendar,
  getCalendarLoaderInfo,
  getCommitments,
  getTasksHeaderInfo,
  getCalendarDialog,
  getCalendarEvents,
  getCalendarView,
  getCalendarInfo,
  getDraggingTask,
  getCommitmentsState,
  getEventError,
  eventsLoading,
  getCalendarStartDate,
  getCalendarEndDate,
  getHolidays,
  isCalendarDataLoading,
  getMonthFilterState,
  getGoogleCalendars,
  getEventApplyDialogState,
  getPlanBlockDialogState,
  isApplyDialogOpen,
  plannedUpdateInProgress,
  getFreeTimeEvents,
  isPlanSidebarCollapsed,
  getCyclicDayCount,
  getAllCourses,
  getAllActivities,
  getClassTimes,
  getTaskEvents,
  getPlannedEvent,
  isCommitmentsLoading,
  isCourseSyncInProgress,
  getSemesterEventsAndAwakes,
  getCommuteTimes,
  getPlannedEvents,
  getDatesContainingTasks,
  getDefaultEventColor,
  isPlanBlockDialogOpen,
  getConnectedCalendarAndPlatformsForCourse,
  getConnectedPlatforms,
  isCheckingForPlatformUpdates,
  isUnsyncCourseFromLmsInProgress,
  isPlanThisWeekInProgress,
  isUnplanWeekInProgress
};
